import { Transition } from "@headlessui/react";
import React, { useCallback, useEffect, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import Logo from "../fragments/icons/Logo";

export const Status = {
  CONNECTING: "CONNECTING",
  OPEN: "OPEN",
  RECONNECTING: "RECONNECTING",
  CLOSING: "CLOSING",
  CLOSED: "CLOSED",
};

export const WebSocketContext = React.createContext({});

const ConnectionOverlay = () => {
  return (
    <div className="fixed z-50 inset-0 bg-gray-600 bg-opacity-80 flex justify-center items-center">
      <div className="flex flex-col items-center drop-shadow-md">
        <div className="bg-primary-600 p-6 w-20 h-20 rounded-full animate-loader">
          <Logo className="text-white w-full h-full animate-loaderlogo" />
        </div>
        <h1 className="mt-2 text-white text-2xl">Connecting</h1>
      </div>
    </div>
  );
};

export const WebSocketProvider = ({ webSocketUrl, heartbeat, children }) => {
  const [status, setStatus] = useState(Status.CLOSED);
  const [connection, setConnection] = useState(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [latency, setLatency] = useState(null);

  useEffect(() => {
    setConnection(() => {
      setStatus(Status.CONNECTING);
      const connection = new ReconnectingWebSocket(webSocketUrl);

      connection.addEventListener("message", (event) => {
        const message = JSON.parse(event.data);
        if (message.type === "heartbeat") {
          setLatency(Math.round(performance.now() - message.pong));
          setShowOverlay(false);
        }
      });

      let heartbeatInterval;
      const sendHeartbeat = () => {
        connection.send(
          JSON.stringify({
            type: "heartbeat",
            ping: performance.now(),
          })
        );
        heartbeatInterval = setTimeout(sendHeartbeat, heartbeat);
      };

      connection.onopen = () => {
        setStatus(Status.OPEN);
        heartbeatInterval = setTimeout(sendHeartbeat);
      };

      connection.onclose = () => {
        setStatus(Status.RECONNECTING);
        if (status !== Status.CLOSING) {
          setShowOverlay(true);
        }
        clearTimeout(heartbeatInterval);
      };

      return connection;
    });

    return () => {
      if (connection) {
        setStatus(Status.CLOSING);
        console.warn(`unmounting`);
        connection.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    connection,
    status,
    latency,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
      <Transition
        show={showOverlay}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <ConnectionOverlay />
      </Transition>
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = ({ messageType, onMessage } = {}) => {
  const { connection, status, latency } = React.useContext(WebSocketContext);

  const sendJson = (message) => {
    if (connection) {
      const data = JSON.stringify(message);
      connection.send(data);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onMessageCallback = useCallback(onMessage, []);

  useEffect(() => {
    const listener = (event) => {
      const data = JSON.parse(event.data);
      if (Array.isArray(data)) {
        data.forEach((message) => {
          if (message.type === messageType) {
            onMessageCallback(message);
          }
        });
      } else {
        if (data.type === messageType) {
          onMessageCallback(data);
        }
      }
    };

    if (connection && messageType && onMessageCallback) {
      connection.addEventListener("message", listener);
    }
    return () => {
      if (connection) {
        connection.removeEventListener("message", listener);
      }
    };
  }, [connection, messageType, onMessageCallback]);

  return { status, latency, sendJson };
};

export default useWebSocket;
